import { useQuery } from '@tanstack/react-query';
import type {
  RevenueChartResponseDto,
  TenantSalesMetricsResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useWithPrevious } from '@/hooks/use-with-previous';
import { useI18nContext } from '@/i18n/i18n-react';
import { appShare } from '@/lib/app-share';
import { computePpDelta } from '@/lib/period-comparison';
import { repurchaseCount } from '@/lib/sales-insights';
import type { SalesDeltas } from '@/components/metrics/metric-sections.types';
import {
  aovChartQuery,
  ordersCompletionQuery,
  repurchaseChartQuery,
  revenueChartQuery,
  salesMetricsQuery,
  tabRange,
  type AnalyticsTabParams,
} from '@/hooks/analytics/analytics-queries';

/**
 * Queries of the Vendas tab, fetched only while it is open (ANLT-01), with
 * the previous period of `/metrics/sales` (variations) and of
 * `/charts/revenue` (the ghost line) (ANLT-04, CMP-01).
 */
export function useSalesTabData(params: AnalyticsTabParams) {
  const tenant = useTenant();
  const { locale } = useI18nContext();
  const range = { ...tabRange(params), preset: params.preset };
  const { granularity, enabled } = params;

  const sales = useWithPrevious<TenantSalesMetricsResponseDto>(
    (r) => ({ ...salesMetricsQuery(tenant.slug, r), enabled }),
    range
  );
  const revenue = useWithPrevious<RevenueChartResponseDto>(
    (r) => ({ ...revenueChartQuery(tenant.slug, r, granularity), enabled }),
    range
  );
  const aovChart = useQuery({
    ...aovChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const repurchaseChart = useQuery({
    ...repurchaseChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const ordersCompletion = useQuery({
    ...ordersCompletionQuery(tenant.slug, range),
    enabled,
  });

  const current = sales.current.data;
  const previous = sales.previous;
  const shareOf = (d: TenantSalesMetricsResponseDto) =>
    appShare(d.totalRevenue, d.web.revenue);
  const deltas: SalesDeltas = {
    revenue: sales.delta((d) => d.totalRevenue),
    // No sales now → no ticket, so no variation (like the Início).
    averageTicket:
      current && current.totalPaidOrders === 0
        ? { kind: 'none' }
        : sales.delta((d) => d.aov),
    orders: sales.delta((d) => d.totalPaidOrders),
    repurchases: sales.delta(repurchaseCount),
    // Previous app + web = 0 → "sem dados no período anterior"; failed or
    // still loading → nothing (CMP-02, CMP-04).
    appShare: current
      ? computePpDelta(
          shareOf(current),
          previous.isError || !previous.data
            ? undefined
            : shareOf(previous.data),
          locale
        )
      : { kind: 'none' },
  };

  return {
    salesMetrics: sales.current,
    salesChart: revenue.current,
    previousSalesChart: revenue.previous,
    aovChart,
    repurchaseChart,
    ordersCompletion,
    deltas,
  };
}
